import { Fragment, useEffect, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { PublicFooter, PublicHeader } from "@/components/PublicChrome";
import SearchBar from "@/components/SearchBar";
import RadarMap from "@/components/RadarMap";
import { RadarMessages } from "@/components/RadarLoading";
import RadarResult, { mapProps } from "@/components/RadarResult";
import Modal from "@/components/Modal";
import SignupSheet from "@/components/SignupSheet";
import LoginChoices from "@/components/LoginChoices";
import { Button } from "@/components/base";
import { useAuth } from "@/lib/auth";
import { api, errCode, errMsg, track } from "@/lib/api";
import { useBotCheck } from "@/lib/botcheck";
import { getAttribution } from "@/lib/utm";
import { tic } from "@/lib/sound";


const SUGGESTIONS = [
  { label: "architecte Paris", metier: "architecte", commune: { code: "75056", nom: "Paris", departement: "75" } },
  { label: "architecte d'intérieur Lyon", metier: "architecte_interieur", commune: { code: "69123", nom: "Lyon", departement: "69" } },
];

function Status({ text, action, onAction, testId }) {
  return (
    <div data-testid={testId} className="fade-up rounded-card border border-line bg-surface px-5 py-6">
      <p className="text-[16px] font-medium text-ink" data-testid={`${testId}-message`}>
        {text}
      </p>
      {action && (
        <Button onClick={onAction} className="mt-4" data-testid={`${testId}-action`}>
          {action}
        </Button>
      )}
    </div>
  );
}

function Suggestions({ onPick }) {
  return (
    <p data-testid="suggestions" className="-mx-4 mt-5 whitespace-nowrap text-center text-[13px] leading-relaxed text-muted sm:mx-0">
      Essaie :{" "}
      {SUGGESTIONS.map((s, i) => (
        <Fragment key={s.commune.code}>
          {i > 0 && (
            <span aria-hidden="true" className="mx-1.5">
              ·
            </span>
          )}
          <button
            type="button"
            data-testid={`suggestion-${s.commune.code}`}
            onClick={() => onPick(s)}
            className="text-ink/75 underline-offset-4 transition-colors duration-150 hover:text-ink hover:underline"
          >
            {s.label}
          </button>
        </Fragment>
      ))}
    </p>
  );
}

export default function Home() {
  const [phase, setPhase] = useState("form");
  const [query, setQuery] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("login") === "1";
    } catch {
      return false;
    }
  });
  const [revealed, setRevealed] = useState(false);
  const [run, setRun] = useState(0);
  const barRef = useRef(null);
  const bot = useBotCheck();
  const { user } = useAuth();
  const openSheet = () => setSignupOpen(true);

  // Reopened from an in-app browser ("Ouvrir dans Safari/Chrome"): same radar, no new search.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("radar");
    if (!id) return;
    window.history.replaceState(null, "", "/");
    api
      .get(`/radar/${id}`)
      .then((r) => {
        setQuery({ metier: r.data.metier, commune: r.data.ville });
        setResult(r.data);
        setRun((n) => n + 1);
        setPhase("result");
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("home-neutral");
    return () => document.documentElement.classList.remove("home-neutral");
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("archijo-home-view")) return;
      sessionStorage.setItem("archijo-home-view", "1");
    } catch {}
    const send = () => api.post("/events/home-view", { attribution: getAttribution() }).catch(() => {});
    if ("requestIdleCallback" in window) window.requestIdleCallback(send, { timeout: 3000 });
    else setTimeout(send, 1200);
  }, []);

  const launch = async (q) => {
    tic();
    setQuery(q);
    setResult(null);
    setRevealed(false);
    setRun((n) => n + 1);
    setPhase("result");
    window.scrollTo({ top: 0 });
    try {
      const token = await bot.getToken();
      const r = await api.post("/radar", {
        metier: q.metier,
        commune_code: q.commune.code,
        city_session: q.commune.session || null,
        city_country: q.commune.country && q.commune.country !== "FR" ? q.commune.country : null,
        bot_token: token,
        attribution: getAttribution(),
      });
      setResult(r.data);
      setPhase(r.data.total_results === 0 ? "empty" : "result");
    } catch (e) {
      const code = errCode(e);
      const known = code === "rate_limited" || code === "invalid_city" || code === "bot_check";
      setError({ code, message: known ? errMsg(e) : "Le radar n'a pas abouti. Réessaie dans un instant." });
      setPhase("error");
    }
  };

  const backToForm = () => {
    setPhase("form");
    window.scrollTo({ top: 0 });
    requestAnimationFrame(() => barRef.current?.focus());
  };

  return (
    <div className="home-neutral dot-surface isolate flex min-h-dvh flex-col">
      <div className="dot-layer dot-fixed" aria-hidden="true" data-testid="home-dots" />
      {phase === "form" ? (
        <section className="relative flex flex-1 flex-col" aria-label="Radar">
          <PublicHeader onLogin={() => setLoginOpen(true)} />
          <div className="relative z-10 flex flex-1 items-center justify-center px-6 pb-[calc(10dvh+8px)] pt-2 sm:px-8 sm:pb-[calc(10dvh+16px)]">
            <div className="fade-up w-full max-w-[880px] text-center" data-testid="hero">
              <p className="num mb-4 text-[12px] font-medium uppercase tracking-[0.16em] text-muted" data-testid="hero-label">
                Radar Google
              </p>
              <h1
                data-testid="hero-title"
                className="mx-auto max-w-[860px] text-balance text-[30px] font-semibold leading-[1.14] tracking-[-0.03em] text-ink sm:text-[44px] sm:leading-[1.1]"
              >
                Qui passe devant toi quand un client cherche un architecte dans ta ville ?
              </h1>
              <div className="mt-7 sm:mt-10">
                <SearchBar ref={barRef} initial={query} onSubmit={launch} />
              </div>
              <Suggestions onPick={launch} />
              {bot.enabled && <div ref={bot.slotRef} data-testid="bot-check-slot" />}
            </div>
          </div>
        </section>
      ) : (
        <section className="relative flex-1" aria-label="Ton radar">
          <PublicHeader onLogin={() => setLoginOpen(true)} />
          <div className="relative z-10 mx-auto max-w-[720px] px-5 pb-10 md:px-0 md:pt-4">
            <div className="flex h-11 items-center gap-1.5 md:mb-2">
              <button
                type="button"
                onClick={backToForm}
                data-testid="new-search-btn"
                aria-label="Nouvelle recherche"
                className="-ml-2 grid h-9 w-9 shrink-0 place-items-center rounded-full text-ink transition-colors duration-150 hover:bg-surface-2"
              >
                <ArrowLeft className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </button>
              <p className="min-w-0 truncate text-[15px] text-ink" data-testid="result-query">
                <span className="text-muted">Ton radar</span>
                <span className="mx-1.5 text-muted">·</span>
                <span className="font-semibold">
                  {query?.metier === "architecte_interieur" ? "Architecte d'intérieur" : "Architecte"} à {query?.commune?.nom}
                </span>
              </p>
            </div>
            {phase === "result" && (
              <>
                <div className="-mx-5 md:mx-0">
                  <RadarMap
                    key={run}
                    intro
                    {...(result ? mapProps(result, openSheet) : {})}
                    radarId={result?.radar_id || null}
                    markers={result?.map?.markers || null}
                    onRevealed={() => {
                      setRevealed(true);
                      if (result?.radar_id) track("result_displayed", { radar_id: result.radar_id });
                    }}
                  />
                </div>
                {!revealed && query && <RadarMessages ville={query.commune.nom} />}
                {revealed && result && (
                  <div className="mt-4 md:mt-6">
                    <RadarResult data={result} withMap={false} onUnlock={openSheet} showPlacer={result.user?.status !== "found"} onPlaced={setResult} />
                  </div>
                )}
              </>
            )}
            {phase === "empty" && (
              <Status testId="radar-empty" text={`Aucun architecte trouvé sur Google à ${query.commune.nom}. Essaie une ville voisine.`} action="Changer de ville" onAction={backToForm} />
            )}
            {phase === "error" && error?.code === "rate_limited" && (
              <Status testId="radar-limit" text={error.message} action="Créer mon compte" onAction={() => setSignupOpen(true)} />
            )}
            {phase === "error" && error?.code !== "rate_limited" && (
              <Status testId="radar-error" text={error?.message} action="Réessayer" onAction={() => launch(query)} />
            )}
          </div>
        </section>
      )}

      <PublicFooter compact />
      <SignupSheet open={signupOpen} onClose={() => setSignupOpen(false)} radarId={phase === "result" ? result?.radar_id : null} profile={query} />
      <Modal open={loginOpen} onClose={() => setLoginOpen(false)} title="Se connecter" testId="login-modal">
        <p className="-mt-2 mb-5 text-[14px] text-muted">Connecte-toi avec Google, ou reçois un code par email.</p>
        <LoginChoices />
      </Modal>
    </div>
  );
}
